/**
 * Auth Store (Zustand)
 *
 * Global authentication state management
 * Handles tokens, user data, and authentication status
 * Following the project's toast.store.ts pattern
 */

import { create } from 'zustand';
import type { AuthStore, LoginResponse, ProviderData } from './auth.types';
import {
  clearTokens,
  decodeJWT,
  getTokens,
  isTokenExpired,
  storeTokens,
} from '@/utils/tokenUtils';

/**
 * Auth Store
 * Manages authentication state, tokens, and user data
 */
export const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial state
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  accessTokenExp: null,
  refreshTokenExp: null,
  claims: null,
  loading: false,
  error: null,

  /**
   * Login action
   * Stores tokens in localStorage and updates store state
   */
  login: (response: LoginResponse) => {
    try {
      // Store tokens in localStorage
      storeTokens(response);

      // Decode JWT to get claims
      const claims = decodeJWT(response.accessToken);

      // Update store state
      set({
        isAuthenticated: true,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        accessTokenExp: response.accessTokenExp,
        refreshTokenExp: response.refreshTokenExp,
        claims,
        error: null,
      });
    } catch (error) {
      console.error('Login failed:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Failed to login. Please try again.',
      });
    }
  },

  /**
   * Logout action
   * Clears auth state and localStorage
   * Note: Call authService.logout() separately to notify server before calling this
   */
  logout: () => {
    get().clearAuth();
  },

  /**
   * Refresh tokens action
   * Updates tokens in localStorage and store state
   */
  refreshTokens: (response: LoginResponse) => {
    try {
      // Store new tokens in localStorage
      storeTokens(response);

      // Decode JWT to get claims
      const claims = decodeJWT(response.accessToken);

      // Update store state with new tokens
      set({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        accessTokenExp: response.accessTokenExp,
        refreshTokenExp: response.refreshTokenExp,
        claims,
        error: null,
      });
    } catch (error) {
      console.error('Token refresh failed:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Failed to refresh tokens. Please login again.',
      });
    }
  },

  /**
   * Set user data
   * Called after fetching provider data from API
   */
  setUser: (user: ProviderData) => {
    set({ user });

    // Cache user info in localStorage
    localStorage.setItem('providerId', user.id);
    localStorage.setItem('providerName', user.businessName);
  },

  /**
   * Check authentication status
   * Validates tokens and restores state from localStorage
   * @returns true if authenticated with valid tokens
   */
  checkAuth: (): boolean => {
    const tokens = getTokens();

    if (!tokens) {
      get().clearAuth();
      return false;
    }

    // Check if access token is expired
    if (isTokenExpired(tokens.accessTokenExp)) {
      // Token expired, clear auth
      get().clearAuth();
      return false;
    }

    // Decode JWT to get claims
    const claims = decodeJWT(tokens.accessToken);

    // Token is valid, restore auth state
    set({
      isAuthenticated: true,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      accessTokenExp: tokens.accessTokenExp,
      refreshTokenExp: tokens.refreshTokenExp,
      claims,
    });

    return true;
  },

  /**
   * Clear all auth state
   * Removes tokens from localStorage and resets store
   */
  clearAuth: () => {
    // Clear localStorage
    clearTokens();

    // Reset store state
    set({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
      accessTokenExp: null,
      refreshTokenExp: null,
      claims: null,
      error: null,
      loading: false,
    });
  },

  /**
   * Set loading state
   */
  setLoading: (loading: boolean) => {
    set({ loading });
  },

  /**
   * Set error state
   */
  setError: (error: string | null) => {
    set({ error });
  },
}));

/**
 * Convenience hook for consuming auth store
 * Provides clean API similar to useToast()
 */
export function useAuth() {
  // Extract state
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const loading = useAuthStore((state) => state.loading);
  const error = useAuthStore((state) => state.error);
  const accessToken = useAuthStore((state) => state.accessToken);
  const accessTokenExp = useAuthStore((state) => state.accessTokenExp);
  const claims = useAuthStore((state) => state.claims);

  // Extract actions
  const {
    login,
    logout,
    refreshTokens,
    setUser,
    checkAuth,
    clearAuth,
    setLoading,
    setError,
  } = useAuthStore();

  return {
    // State
    isAuthenticated,
    user,
    loading,
    error,
    accessToken,
    accessTokenExp,
    claims,

    // Actions
    login,
    logout,
    refreshTokens,
    setUser,
    checkAuth,
    clearAuth,
    setLoading,
    setError,
  };
}
